import type { AdminTwin, MapOrigin } from '@repo/frontend-types';

/**
 * WHERE ON EARTH should this twin be activated?
 *
 * A twin taken through the wizard alone used to land at **null island**. A twin is created with no
 * latitude or longitude — the retired Create New Twin dialog wrote none, and Create Twin
 * (`twin-details-page-create`) writes none by rule — and Go Live's origin resolution was:
 *
 * ```ts
 * const mapOrigin = prevModel?.mapOrigin ? { …prevModel.mapOrigin } : { latitude: 0, longitude: 0 };
 * ```
 *
 * so a first activation put the whole model in the Gulf of Guinea, and the only cure was the
 * twin-viewer's Edit mode — exactly the dependency the twin-onboarding epic exists to remove.
 *
 * The chain is now **prior-model origin → twin-details lat/lng → block**, and it never falls back
 * to 0,0. The prior model counts only once it holds an entity: twin creation writes an empty root
 * whose origin is only a copy of the last position Twin details saved, and clearing that position
 * nulls the record but not the root (the middleware's dGraph fan-out skips a null pair). Left to the
 * root, a twin whose position was cleared would launch at the cleared place
 * (`twin-details-page-address`, Copilot review).
 *
 * ---------------------------------------------------------------------------------------
 * WHY "IS THERE A PRIOR mapOrigin" IS THE WRONG QUESTION.
 *
 * The spec called the prior-model branch "existing behaviour, unchanged". Taken literally that
 * leaves the feature's own acceptance criterion unmet: every twin the wizard has ALREADY
 * activated has a prior model whose `mapOrigin` **is** (0,0), so a presence check matches and
 * re-activates it at null island for ever. The predicate has to be *usable*, not *present*.
 *
 * `isUsableOrigin` therefore rejects the (0,0) PAIR. Only the pair — `longitude: 0` alone is
 * Greenwich and entirely legitimate (Newport Live's own twins sit within a degree of it), so
 * rejecting a zero longitude would break real data. There is no twin at 0°N 0°E.
 * ---------------------------------------------------------------------------------------
 */

export type ActivationOriginSource =
  /** The twin's existing dGraph model — an established twin keeps the origin it has. */
  | 'model'
  /** The twin record's own latitude/longitude, set in the wizard's Twin details step. */
  | 'twinDetails';

export type ActivationOriginOutcome =
  | { status: 'ok'; origin: MapOrigin; source: ActivationOriginSource }
  /** No usable origin anywhere. Activation must stop rather than site the twin at 0,0. */
  | { status: 'blocked' };

const LAT_RANGE = 90;
const LNG_RANGE = 180;

/**
 * Is this pair a real place we can anchor a model to?
 *
 * Values arrive as **strings** from the twin record (the `twin` columns are numeric, but the API
 * types and the PATCH DTO both carry them as strings) and as numbers from a dGraph `mapOrigin`,
 * so the coercion is deliberate rather than defensive.
 */
export const isUsableOrigin = (
  latitude: number | string | null | undefined,
  longitude: number | string | null | undefined,
): boolean => {
  // `Number('')` is 0 and `Number(null)` is 0 — both would sail through a finite check and land
  // on null island, which is the precise failure this guard exists to stop.
  if (latitude == null || longitude == null || latitude === '' || longitude === '') return false;

  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -LAT_RANGE || lat > LAT_RANGE) return false;
  if (lng < -LNG_RANGE || lng > LNG_RANGE) return false;

  // Null island. See the note above: the PAIR only.
  if (lat === 0 && lng === 0) return false;

  return true;
};

export interface ResolveActivationOriginParams {
  /**
   * The twin's current dGraph model, or null on a first activation. Only `mapOrigin` is read;
   * typed loosely because the caller bridges two generated `Entity` types at this point.
   */
  prevModel:
    | {
        mapOrigin?: { latitude?: number; longitude?: number } | null;
        /** The model's top-level entities; none means the empty root twin creation wrote. */
        children?: readonly unknown[] | null;
      }
    | null
    | undefined;
  /**
   * The twin's AdminDB record — already fetched by Go Live's step 0 (`resolveActivationOrg`
   * resolves the namespace from it), so this costs no extra round trip.
   */
  twin: Pick<AdminTwin, 'latitude' | 'longitude'> | null | undefined;
}

export const resolveActivationOrigin = ({
  prevModel,
  twin,
}: ResolveActivationOriginParams): ActivationOriginOutcome => {
  // 1. The established model wins. An existing twin keeps the origin it is already rendered at,
  //    so re-activating never moves a live model out from under whoever is looking at it — and a
  //    twin built by FML import or the admin planner, which may have no address on its record at
  //    all, is unaffected by this feature.
  //    The empty root twin creation wrote is not established, so the record decides for it.
  const established = (prevModel?.children?.length ?? 0) > 0;
  const modelLat = prevModel?.mapOrigin?.latitude;
  const modelLng = prevModel?.mapOrigin?.longitude;
  if (established && isUsableOrigin(modelLat, modelLng)) {
    return {
      status: 'ok',
      origin: { latitude: Number(modelLat), longitude: Number(modelLng) },
      source: 'model',
    };
  }

  // 2. The wizard's Twin details step. Also the repair path for a twin already stranded at 0,0:
  //    its model origin is unusable, so this branch re-sites it on the next Go Live.
  if (isUsableOrigin(twin?.latitude, twin?.longitude)) {
    return {
      status: 'ok',
      origin: { latitude: Number(twin!.latitude), longitude: Number(twin!.longitude) },
      source: 'twinDetails',
    };
  }

  return { status: 'blocked' };
};

/**
 * User-facing copy for a blocked activation. Kept here so the tests pin the wording, and so the
 * message always names the step that fixes it — a bare "cannot activate" would send someone back
 * to Edit mode, which is the dependency this epic is removing.
 */
export const activationOriginErrorMessage = (): string =>
  'This twin has no location yet, so activation was stopped before anything was changed. Open the ' +
  'Twin details step, set the address or the latitude and longitude, then try again.';
